import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import { inspectorService } from '../services/inspectorService';
import { storageService } from '../services/storageService';
import { useAuth } from '../context/AuthContext';
import { InspectorLoadingOverlay } from '../components/InspectorLoadingOverlay';
import ImageViewerModal from '../components/ImageViewerModal';
import { useBackHandler } from '../hooks/useBackHandler';
import { formatInspectorError } from '../utils/errorUtils';
import { isSimulatorAllowed } from '../utils/envUtils';
import { getMediaBaseUrl } from '../config';

// Catálogo de incidencias
const PE_INCIDENCES = [
    "TAPA DAÑADA",
    "REGISTRO SIN TAPA",
    "REGISTRO OBTRUIDO",
    "REGISTRO DETERIORADO",
    "REGISTRO ANEGADO",
    "CIERRE NO HOMOLOGADO PARA REGISTRO",
    "REGISTRO NO HOMOLOGADO (MEDIDAS INCORRECTAS, SIN RADIALES)"
];

const PI_INCIDENCES = [
    "ACOPLADORES MECANICOS EN TRAYECTORIA",
    "DROP FUERA DE RADIALES",
    "DROP FUERA DE NORMA EN VERTICAL"
];

const AEREO_PI_INCIDENCES = [
    "ACOMETIDA EN LUMINARIA",
    "SIN BOTA PARA CAJA 2N OPTITAP"
];

const haversineMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const phi1 = (parseFloat(lat1) * Math.PI) / 180;
    const phi2 = (parseFloat(lat2) * Math.PI) / 180;
    const deltaPhi = ((parseFloat(lat2) - parseFloat(lat1)) * Math.PI) / 180;
    const deltaLambda = ((parseFloat(lon2) - parseFloat(lon1)) * Math.PI) / 180;
    const a = Math.sin(deltaPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
};

const CustomAlertModal = ({ show, title, message, type = 'info', onClose }) => {
    if (!show) return null;

    const getIcon = () => {
        switch (type) {
            case 'success': return '✅';
            case 'error': return '❌';
            case 'warning': return '⚠️';
            case 'offline': return '📴';
            default: return 'ℹ️';
        }
    };

    const getHeaderBg = () => {
        switch (type) {
            case 'success': return 'linear-gradient(135deg, #059669, #10b981)';
            case 'error': return 'linear-gradient(135deg, #dc2626, #ef4444)';
            case 'warning': return 'linear-gradient(135deg, #d97706, #f59e0b)';
            case 'offline': return 'linear-gradient(135deg, #ea580c, #f97316)';
            default: return 'linear-gradient(135deg, #2563eb, #3b82f6)';
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.75)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <div style={{
                background: '#ffffff', borderRadius: '18px', width: '90%', maxWidth: '400px',
                overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                animation: 'popIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
            }}>
                <div style={{
                    background: getHeaderBg(), color: '#ffffff', padding: '16px 20px',
                    display: 'flex', alignItems: 'center', gap: '10px'
                }}>
                    <span style={{ fontSize: '24px' }}>{getIcon()}</span>
                    <span style={{ fontWeight: 800, fontSize: '16px' }}>{title || 'Notificación'}</span>
                </div>
                <div style={{ padding: '20px', color: '#1e293b', fontSize: '14px', lineHeight: '1.5', fontWeight: 500, whiteSpace: 'pre-line' }}>
                    {message}
                </div>
                <div style={{ padding: '12px 20px 18px 20px', textAlign: 'right' }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: getHeaderBg(), color: '#ffffff', border: 'none',
                            padding: '10px 24px', borderRadius: '12px', fontWeight: 700,
                            fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};

const InspectorPEMapModal = ({
    workdayId,
    initialSegments = [],
    onClose,
    onSegmentAdded,
    simulatedLocation = null,
    simulatedTimeOffsetMinutes = 0,
    startLocation = null
}) => {
    const { user } = useAuth();

    const handleClose = () => {
        if (onClose) onClose(segments);
    };

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const polylineRef = useRef(null);
    const inspectorMarkerRef = useRef(null);
    const watchIdRef = useRef(null);
    const hasCenteredRef = useRef(false);
    const createdObjectUrlsRef = useRef([]);

    const [segments, setSegments] = useState(initialSegments);
    const [currentPosition, setCurrentPosition] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [syncingSegId, setSyncingSegId] = useState(null);
    const [customAlert, setCustomAlert] = useState({ show: false, title: '', message: '', type: 'info' });

    const showAlert = (message, title = 'Notificación', type = 'info') => {
        setCustomAlert({ show: true, title, message, type });
    };
    const hideAlert = () => {
        setCustomAlert(prev => ({ ...prev, show: false }));
    };

    // Formulario de registro flotante
    const [showRegForm, setShowRegForm] = useState(false);
    const [networkType, setNetworkType] = useState('aereo'); // 'aereo' | 'canalizado'
    const [selectedIncidences, setSelectedIncidences] = useState([]);

    // Evidencia fotográfica para canalizado
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [compressing, setCompressing] = useState(false);
    const [expandedImage, setExpandedImage] = useState(null);

    useBackHandler(showRegForm, () => setShowRegForm(false), 'showRegForm');

    const getLocalObjectUrl = useCallback((blob) => {
        if (!blob || !(blob instanceof Blob) || blob.size === 0) return null;
        try {
            const url = URL.createObjectURL(blob);
            createdObjectUrlsRef.current.push(url);
            return url;
        } catch (e) {
            console.warn("No se pudo crear object URL para miniatura:", e);
            return null;
        }
    }, []);

    // Limpieza de Object URLs al desmontar
    useEffect(() => {
        return () => {
            createdObjectUrlsRef.current.forEach(url => {
                try { URL.revokeObjectURL(url); } catch (_) {}
            });
            createdObjectUrlsRef.current = [];
        };
    }, []);

    useEffect(() => {
        window.expandPEMapPhoto = (url) => {
            setExpandedImage(url);
        };
        return () => {
            delete window.expandPEMapPhoto;
        };
    }, []);

    const calcDistances = useCallback((segs) => {
        let total = 0;
        let efectiva = 0;

        if (startLocation && segs.length > 0) {
            const firstSeg = segs[0];
            const distInicio = haversineMeters(
                startLocation.latitude, startLocation.longitude,
                firstSeg.latitude, firstSeg.longitude
            );
            if (distInicio < 500) {
                total += distInicio;
                if (distInicio <= 150) {
                    efectiva += distInicio;
                }
            }
        }

        for (let i = 1; i < segs.length; i++) {
            const p1 = segs[i - 1];
            const p2 = segs[i];
            const d = haversineMeters(p1.latitude, p1.longitude, p2.latitude, p2.longitude);
            total += d;
            if (d <= 150) {
                efectiva += d;
            }
        }

        return { total: Math.round(total), efectiva: Math.round(efectiva) };
    }, [startLocation]);

    const initialDist = calcDistances(initialSegments);
    const [totalDistance, setTotalDistance] = useState(initialDist.total);
    const [effectiveDistance, setEffectiveDistance] = useState(initialDist.efectiva);

    const recalcTotal = useCallback((segs) => {
        const d = calcDistances(segs);
        setTotalDistance(d.total);
        setEffectiveDistance(d.efectiva);
        return d.total;
    }, [calcDistances]);

    // Recuperar segmentos offline guardados en IndexedDB para esta jornada
    useEffect(() => {
        const loadOfflineSegments = async () => {
            if (!workdayId) return;
            try {
                const offlineItems = await storageService.getPendingInspectorSegments(workdayId);
                if (offlineItems && offlineItems.length > 0) {
                    setSegments(prev => {
                        const existingIds = new Set(prev.map(s => String(s.id)));
                        const newOfflineSegs = offlineItems
                            .filter(item => !existingIds.has(`offline_${item.id}`))
                            .map(item => ({
                                id: `offline_${item.id}`,
                                offlineId: item.id,
                                latitude: item.latitude,
                                longitude: item.longitude,
                                recorded_at: item.recorded_at,
                                distance_from_previous: item.distance_from_previous || 0,
                                network_type: item.network_type,
                                incidences: item.incidences || [],
                                photo_blob: item.photo_blob,
                                is_offline: true,
                                _localUrl: item.photo_blob ? getLocalObjectUrl(item.photo_blob) : null
                            }));
                        const combined = [...prev, ...newOfflineSegs];
                        setTotalDistance(recalcTotal(combined));
                        return combined;
                    });
                }
            } catch (err) {
                console.error("Error cargando segmentos offline de inspector:", err);
            }
        };
        loadOfflineSegments();
    }, [workdayId, recalcTotal, getLocalObjectUrl]);

    // Clean up previews
    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    // El simulador solo se habilita en web staging o localhost de desarrollo, NUNCA en APK móvil ni en producción
    const isSimulatorEnabled = isSimulatorAllowed();

    // Watch position (GPS)
    useEffect(() => {
        if (isSimulatorEnabled && simulatedLocation) {
            setCurrentPosition(simulatedLocation);
            return;
        }

        if (navigator.geolocation) {
            watchIdRef.current = navigator.geolocation.watchPosition(
                (pos) => setCurrentPosition({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
                (err) => console.error('Geo error:', err),
                { enableHighAccuracy: true, maximumAge: 5000 }
            );
        }
        return () => { if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current); };
    }, [simulatedLocation, isSimulatorEnabled]);

    // Cache de iconos para evitar regeneración pesada de SVG/DOM por punto
    const iconCacheRef = useRef({
        aereo_normal: L.divIcon({
            className: 'custom-premium-marker',
            html: `<svg viewBox="0 0 36 46" width="36" height="46" style="filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.35));"><path d="M18 0C8.06 0 0 8.06 0 18c0 10.5 18 26 18 26s18-15.5 18-26C36 8.06 27.94 0 18 0z" fill="#2563eb" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/><path d="M18 10v20 M10 14h16 M13 20h10" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/></svg>`,
            iconSize: [36, 46], iconAnchor: [18, 44], popupAnchor: [0, -42]
        }),
        aereo_incidences: L.divIcon({
            className: 'custom-premium-marker',
            html: `<svg viewBox="0 0 36 46" width="36" height="46" style="filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.35));"><path d="M18 0C8.06 0 0 8.06 0 18c0 10.5 18 26 18 26s18-15.5 18-26C36 8.06 27.94 0 18 0z" fill="#ef4444" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/><path d="M18 10v20 M10 14h16 M13 20h10" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/></svg>`,
            iconSize: [36, 46], iconAnchor: [18, 44], popupAnchor: [0, -42]
        }),
        canalizado_normal: L.divIcon({
            className: 'custom-premium-marker',
            html: `<svg viewBox="0 0 36 46" width="36" height="46" style="filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.35));"><path d="M6 0h24c3.3 0 6 2.7 6 6v12c0 3.3-2.7 6-6 6H22.5L18 44 L13.5 24 H6c-3.3 0-6-2.7-6-6V6c0-3.3 2.7-6 6-6z" fill="#2563eb" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/><path d="M11 9h14v12H11z M11 15h14 M18 9v12" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/></svg>`,
            iconSize: [36, 46], iconAnchor: [18, 44], popupAnchor: [0, -42]
        }),
        canalizado_incidences: L.divIcon({
            className: 'custom-premium-marker',
            html: `<svg viewBox="0 0 36 46" width="36" height="46" style="filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.35));"><path d="M6 0h24c3.3 0 6 2.7 6 6v12c0 3.3-2.7 6-6 6H22.5L18 44 L13.5 24 H6c-3.3 0-6-2.7-6-6V6c0-3.3 2.7-6 6-6z" fill="#ef4444" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/><path d="M11 9h14v12H11z M11 15h14 M18 9v12" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/></svg>`,
            iconSize: [36, 46], iconAnchor: [18, 44], popupAnchor: [0, -42]
        })
    });

    const getCachedIcon = useCallback((type, incidences = []) => {
        const hasInc = incidences && incidences.length > 0;
        const isAereo = type === 'aereo';
        if (isAereo) {
            return hasInc ? iconCacheRef.current.aereo_incidences : iconCacheRef.current.aereo_normal;
        } else {
            return hasInc ? iconCacheRef.current.canalizado_incidences : iconCacheRef.current.canalizado_normal;
        }
    }, []);

    // Init map UNA SOLA VEZ al montar el componente
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        const map = L.map(mapRef.current, {
            maxZoom: 22,
            zoomControl: true,
            preferCanvas: true,
            fadeAnimation: false, // Evita parpadeos de azulejos en móviles
            markerZoomAnimation: true
        }).setView([25.686, -100.316], 15);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OSM',
            maxZoom: 22,
            maxNativeZoom: 19,
            updateWhenIdle: true,
            keepBuffer: 3
        }).addTo(map);

        mapInstance.current = map;

        // Invalidate size para solucionar cuadro gris por flexbox/modal layout
        const timer1 = setTimeout(() => { if (mapInstance.current) mapInstance.current.invalidateSize(); }, 200);
        const timer2 = setTimeout(() => { if (mapInstance.current) mapInstance.current.invalidateSize(); }, 600);
        const timer3 = setTimeout(() => { if (mapInstance.current) mapInstance.current.invalidateSize(); }, 1200);

        let resizeObserver = null;
        if (window.ResizeObserver && mapRef.current) {
            resizeObserver = new ResizeObserver(() => {
                if (mapInstance.current) mapInstance.current.invalidateSize();
            });
            resizeObserver.observe(mapRef.current);
        }

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
            if (resizeObserver) resizeObserver.disconnect();
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, []);

    // Renderizar marcadores de segmentos y polyline ÚNICAMENTE cuando cambia 'segments'
    useEffect(() => {
        const map = mapInstance.current;
        if (!map) return;

        // Limpiar marcadores anteriores
        markersRef.current.forEach(m => map.removeLayer(m));
        markersRef.current = [];

        if (polylineRef.current) {
            map.removeLayer(polylineRef.current);
            polylineRef.current = null;
        }

        const baseUrl = getMediaBaseUrl();

        const coords = [];
        segments.forEach((seg, i) => {
            const lat = parseFloat(seg.latitude);
            const lng = parseFloat(seg.longitude);
            if (isNaN(lat) || isNaN(lng)) return;

            coords.push([lat, lng]);

            const marker = L.marker([lat, lng], {
                icon: getCachedIcon(seg.network_type, seg.incidences)
            }).addTo(map);

            const hasInc = seg.incidences && seg.incidences.length > 0;
            const incText = hasInc ? seg.incidences.join(', ') : 'Ninguna';

            const photoUrl = seg.is_offline && seg.photo_blob
                ? (seg._localUrl || (seg._localUrl = getLocalObjectUrl(seg.photo_blob)))
                : seg.photo_path
                    ? `${baseUrl}/static/uploads/${seg.photo_path}`
                    : null;

            const photoHtml = photoUrl
                ? `<div style="margin-top:8px;" onclick="window.expandPEMapPhoto && window.expandPEMapPhoto('${photoUrl}')">` +
                `<img src="${photoUrl}" loading="lazy" decoding="async" style="width: 100%; max-height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #ddd; cursor: pointer;" />` +
                `</div>`
                : '';

            const statusHtml = seg.is_offline ? '<br/><b style="color: #f59e0b;">📴 Offline (Pendiente)</b>' : '';

            marker.bindPopup(
                `<div style="font-family: system-ui, sans-serif; font-size: 13px; line-height: 1.4; color: #333; min-width: 180px;">` +
                `<b style="font-size: 14px; color: #1e3a8a;">Punto #${i + 1}</b>${statusHtml}<br/>` +
                `<hr style="border: none; border-top: 1px solid #ddd; margin: 6px 0;" />` +
                `<b>Red:</b> ${seg.network_type === 'aereo' ? '⚡ Aéreo' : '⛖ Canalizado'}<br/>` +
                `<b>Incidencias:</b> <span style="color: ${hasInc ? '#dc2626' : '#2563eb'}; font-weight: 600;">${incText}</span><br/>` +
                `<b>Dist. anterior:</b> ${seg.distance_from_previous || 0}m<br/>` +
                `<span style="font-size: 11px; color: #777;">Hora: ${new Date(seg.recorded_at).toLocaleTimeString('es-MX')}</span>` +
                photoHtml +
                `</div>`
            );
            markersRef.current.push(marker);
        });

        // Dibujar trayectoria
        if (coords.length >= 2) {
            polylineRef.current = L.polyline(coords, { color: '#2196F3', weight: 4, opacity: 0.85, dashArray: '5, 10' }).addTo(map);
        }

        // Ajustar encuadre inicial solo 1 vez para no congelar zoom del usuario
        if (coords.length > 0 && !hasCenteredRef.current) {
            hasCenteredRef.current = true;
            if (coords.length === 1) {
                map.setView(coords[0], 16);
            } else if (polylineRef.current) {
                map.fitBounds(polylineRef.current.getBounds(), { padding: [40, 40] });
            }
        }
    }, [segments, getCachedIcon, getLocalObjectUrl]);

    // Actualizar ÚNICAMENTE la posición del inspector sin tocar ni destruir los marcadores del recorrido
    useEffect(() => {
        const map = mapInstance.current;
        if (!map || !currentPosition) return;

        const latlng = [currentPosition.latitude, currentPosition.longitude];

        if (inspectorMarkerRef.current) {
            inspectorMarkerRef.current.setLatLng(latlng);
        } else {
            inspectorMarkerRef.current = L.circleMarker(
                latlng,
                { radius: 10, fillColor: '#4CAF50', color: '#fff', weight: 3, fillOpacity: 1 }
            ).addTo(map);
            inspectorMarkerRef.current.bindPopup('📍 Tu ubicación actual');

            if (segments.length === 0 && !hasCenteredRef.current) {
                hasCenteredRef.current = true;
                map.setView(latlng, 16);
            }
        }
    }, [currentPosition, segments.length]);

    const compressImage = (file, maxSizeMB = 0.4) => {
        return new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                // Liberar el Object URL inmediatamente para liberar RAM
                URL.revokeObjectURL(objectUrl);

                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                const maxWidth = 1000;
                const maxHeight = 1000;
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // --- ESTAMPAR INFORMACIÓN DE GEOLOCALIZACIÓN Y TÉCNICO (OPENCAM STYLE) ---
                const bannerHeight = Math.round(height * 0.16); // 16% del alto
                ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                ctx.fillRect(0, height - bannerHeight, width, bannerHeight);

                // Línea azul de acento superior en el banner
                ctx.fillStyle = '#2563eb';
                ctx.fillRect(0, height - bannerHeight, width, Math.max(3, Math.round(height * 0.005)));

                // Ajustes de texto
                ctx.fillStyle = '#ffffff';
                ctx.font = `bold ${Math.round(bannerHeight * 0.13)}px system-ui, -apple-system, sans-serif`;
                ctx.textBaseline = 'top';

                const getSimulatedDate = () => {
                    const d = new Date();
                    if (isSimulatorEnabled && simulatedTimeOffsetMinutes !== 0) {
                        d.setTime(d.getTime() + simulatedTimeOffsetMinutes * 60 * 1000);
                    }
                    return d;
                };

                const nameText = `Inspector: ${user?.full_name || 'N/A'} (${user?.username || 'N/A'})`;
                const regionText = `Región: ${user?.region || 'N/A'} | Distrito: ${user?.distrito || user?.district || 'N/A'}`;
                const dateText = `Fecha/Hora: ${getSimulatedDate().toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}`;
                const gpsText = `Coordenadas GPS: ${currentPosition?.latitude?.toFixed(6) || 'N/A'}, ${currentPosition?.longitude?.toFixed(6) || 'N/A'}`;

                const startX = Math.round(width * 0.04); // 4% de margen izquierdo
                const lineSpacing = Math.round(bannerHeight * 0.21);
                const startY = height - bannerHeight + Math.round(bannerHeight * 0.10);

                ctx.fillText(nameText, startX, startY);
                ctx.fillText(regionText, startX, startY + lineSpacing);
                ctx.fillText(dateText, startX, startY + lineSpacing * 2);
                ctx.fillText(gpsText, startX, startY + lineSpacing * 3);

                // Compresión eficiente en máximo 2 pasos para ahorrar CPU y RAM
                const quality = 0.70; // 0.70 es excelente calidad visual y tamaño muy optimizado (~120KB)
                const cleanupCanvas = () => {
                    canvas.width = 0;
                    canvas.height = 0;
                    img.onload = null;
                    img.onerror = null;
                    img.src = '';
                };
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            cleanupCanvas();
                            reject(new Error('Error al generar blob de la imagen'));
                            return;
                        }
                        const sizeMB = blob.size / (1024 * 1024);
                        if (sizeMB > maxSizeMB) {
                            canvas.toBlob(
                                (secondBlob) => {
                                    cleanupCanvas();
                                    if (!secondBlob) {
                                        resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                                        return;
                                    }
                                    console.log(`📦 [Recorrido] Compresión (Paso 2): ${(file.size / 1024).toFixed(1)} KB -> ${(secondBlob.size / 1024).toFixed(1)} KB`);
                                    resolve(new File([secondBlob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                                },
                                'image/jpeg',
                                0.50
                            );
                        } else {
                            cleanupCanvas();
                            console.log(`📦 [Recorrido] Compresión (Paso 1): ${(file.size / 1024).toFixed(1)} KB -> ${(blob.size / 1024).toFixed(1)} KB`);
                            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Error al procesar la imagen'));
            };
            img.src = objectUrl;
        });
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        const f = files[0];

        if (!f.type.startsWith('image/')) {
            showAlert('Por favor selecciona una imagen válida.', 'Foto de Evidencia', 'warning');
            return;
        }

        if (preview) {
            URL.revokeObjectURL(preview);
        }

        setCompressing(true);
        compressImage(f, 0.4).then(compressedFile => {
            setPhoto(compressedFile);
            setPreview(URL.createObjectURL(compressedFile));
            setErrorMsg('');
        }).catch(err => {
            console.error(err);
            setPhoto(f);
            setPreview(URL.createObjectURL(f));
            setErrorMsg('');
        }).finally(() => {
            setCompressing(false);
            e.target.value = '';
        });
    };

    const removePhoto = () => {
        if (preview) URL.revokeObjectURL(preview);
        setPhoto(null);
        setPreview(null);
    };

    const handleOpenRegForm = () => {
        if (!currentPosition) { showAlert('Esperando ubicación GPS de alta precisión...', 'Geolocalización', 'warning'); return; }
        setNetworkType('aereo');
        setSelectedIncidences([]);
        setPhoto(null);
        setPreview(null);
        setErrorMsg('');
        setShowRegForm(true);
    };

    const handleToggleIncidence = (inc) => {
        setSelectedIncidences(prev =>
            prev.includes(inc) ? prev.filter(i => i !== inc) : [...prev, inc]
        );
    };

    const savePointOffline = async (calcDistance, simTime) => {
        const pendingOffline = await storageService.getPendingInspectorSegments(workdayId);
        if (pendingOffline.length >= 3) {
            const msg = "Tienes 3 segmentos guardados en modo offline pendientes de envío. Por favor busca una zona con buena señal para enviar un segmento antes de registrar uno nuevo.";
            showAlert(msg, "Límite de 3 Segmentos Offline", "warning");
            setErrorMsg("Límite de 3 segmentos offline alcanzado. Busca zona con señal para enviar los pendientes.");
            return;
        }

        const recAt = simTime || new Date().toISOString();
        const offlineData = {
            workdayId: workdayId,
            latitude: currentPosition.latitude.toString(),
            longitude: currentPosition.longitude.toString(),
            network_type: networkType,
            incidences: selectedIncidences,
            photo_blob: photo,
            recorded_at: recAt,
            distance_from_previous: calcDistance,
            is_offline: true
        };

        const offlineId = await storageService.saveInspectorSegment(offlineData);
        const safeOfflineId = offlineId || `off_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        const newOfflineSeg = {
            ...offlineData,
            id: `offline_${safeOfflineId}`,
            offlineId: safeOfflineId,
            _localUrl: photo ? getLocalObjectUrl(photo) : null
        };

        const updated = [...segments, newOfflineSeg];
        setSegments(updated);
        setTotalDistance(recalcTotal(updated));
        if (onSegmentAdded) {
            onSegmentAdded(
                {
                    segment_count: updated.length,
                    total_distance_meters: recalcTotal(updated)
                },
                newOfflineSeg
            );
        }
        if (mapInstance.current) {
            mapInstance.current.setView([parseFloat(newOfflineSeg.latitude), parseFloat(newOfflineSeg.longitude)], 16);
        }
        setShowRegForm(false);
        if (preview) URL.revokeObjectURL(preview);
        setPhoto(null);
        setPreview(null);
        showAlert("Sin señal o falla de red: El punto fue guardado en Modo Offline (Pendiente de envío).", "Modo Offline", "offline");
    };

    const handleSavePoint = async () => {
        if (!photo) {
            setErrorMsg(`La foto de evidencia es obligatoria para red ${networkType === 'canalizado' ? 'canalizada' : 'aérea'}.`);
            return;
        }
        setRegistering(true);
        setErrorMsg('');

        let calcDist = 0;
        let lastPointLoc = null;
        if (segments.length > 0) {
            lastPointLoc = segments[segments.length - 1];
            calcDist = haversineMeters(
                lastPointLoc.latitude, lastPointLoc.longitude,
                currentPosition.latitude, currentPosition.longitude
            );
        } else if (startLocation) {
            lastPointLoc = startLocation;
            const distInit = haversineMeters(
                startLocation.latitude, startLocation.longitude,
                currentPosition.latitude, currentPosition.longitude
            );
            calcDist = distInit < 500 ? distInit : 0;
        }

        // Candado de distancia mínima (< 5 metros)
        if (lastPointLoc) {
            const distReal = haversineMeters(
                lastPointLoc.latitude, lastPointLoc.longitude,
                currentPosition.latitude, currentPosition.longitude
            );
            if (distReal < 5) {
                const confirm = window.confirm(`⚠️ ADVERTENCIA: Estás marcando este punto a solo ${distReal.toFixed(1)} metros del punto anterior.\n\nParece que tu GPS no ha detectado movimiento o sigues en el mismo lugar.\n\n¿Estás completamente seguro de registrar el punto aquí?`);
                if (!confirm) {
                    setRegistering(false);
                    return;
                }
            }
        }

        const simTime = (isSimulatorEnabled && simulatedTimeOffsetMinutes !== 0)
            ? (() => {
                const d = new Date();
                d.setTime(d.getTime() + simulatedTimeOffsetMinutes * 60 * 1000);
                return d.toISOString();
            })()
            : null;

        if (!navigator.onLine) {
            await savePointOffline(calcDist, simTime);
            setRegistering(false);
            return;
        }

        try {
            const result = await inspectorService.registerPESegment(
                workdayId,
                currentPosition.latitude,
                currentPosition.longitude,
                networkType,
                selectedIncidences,
                photo,
                simTime
            );
            if (result.success) {
                const newSeg = {
                    id: result.segment_id,
                    latitude: result.latitude,
                    longitude: result.longitude,
                    recorded_at: result.recorded_at,
                    distance_from_previous: result.distance_from_previous,
                    network_type: result.network_type,
                    incidences: result.incidences,
                    photo_path: result.photo_path,
                    is_offline: false
                };
                const updated = [...segments, newSeg];
                setSegments(updated);
                setTotalDistance(recalcTotal(updated));
                if (onSegmentAdded) onSegmentAdded(result.workday_totals, newSeg);

                // Center map on new point
                if (mapInstance.current) {
                    mapInstance.current.setView([parseFloat(result.latitude), parseFloat(result.longitude)], 16);
                }
                setShowRegForm(false);
                if (preview) URL.revokeObjectURL(preview);
                setPhoto(null);
                setPreview(null);
            }
        } catch (err) {
            console.warn("⚠️ Falló envío a servidor, guardando segmento offline:", err);
            await savePointOffline(calcDist, simTime);
        } finally {
            setRegistering(false);
        }
    };

    const handleSyncSingleSegment = async (segToSync) => {
        if (syncingSegId) return; // Evita enviar simultáneamente múltiples puntos y colisionar
        // Verificar conectividad antes de intentar enviar
        if (!navigator.onLine) {
            showAlert('Tu teléfono se encuentra sin conexión a internet. Acércate a una zona con cobertura e intenta nuevamente.', 'Modo Fuera de Línea', 'warning');
            return;
        }
        setSyncingSegId(segToSync.id);
        try {
            const result = await inspectorService.registerPESegment(
                workdayId,
                segToSync.latitude,
                segToSync.longitude,
                segToSync.network_type,
                segToSync.incidences,
                segToSync.photo_blob,
                segToSync.recorded_at
            );
            if (result.success) {
                if (segToSync.offlineId) {
                    await storageService.deleteInspectorSegment(segToSync.offlineId);
                }
                const updatedSeg = {
                    id: result.segment_id,
                    latitude: result.latitude,
                    longitude: result.longitude,
                    recorded_at: result.recorded_at,
                    distance_from_previous: result.distance_from_previous,
                    network_type: result.network_type,
                    incidences: result.incidences,
                    photo_path: result.photo_path,
                    is_offline: false
                };
                const updatedList = segments.map(s => s.id === segToSync.id ? updatedSeg : s);
                setSegments(updatedList);
                setTotalDistance(recalcTotal(updatedList));
                if (onSegmentAdded) {
                    onSegmentAdded(result.workday_totals, updatedSeg);
                }
                showAlert("Segmento offline enviado y registrado exitosamente con el servidor.", "Sincronizado", "success");
            }
        } catch (err) {
            showAlert("No se pudo enviar el segmento offline.\n\n" + formatInspectorError(err), "Error al Enviar", "error");
        } finally {
            setSyncingSegId(null);
        }
    };

    const baseUrl = getMediaBaseUrl();

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 3000,
            display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif'
        }}>
            {/* Map area */}
            <div style={{ flex: 1, position: 'relative' }}>
                <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

                {/* Top bar */}
                <div style={{
                    position: 'absolute', top: 10, left: 10, right: 10, zIndex: 1000,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <div style={{
                        background: 'rgba(26, 26, 46, 0.9)', color: '#fff', padding: '10px 18px',
                        borderRadius: 12, fontWeight: 700, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        🗺️ Recorrido PE / PI — Puntos: {segments.length}
                    </div>
                    <button onClick={handleClose} style={{
                        background: '#dc3545', color: '#fff', border: 'none', padding: '10px 20px',
                        borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 14,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)', transition: 'background 0.2s'
                    }}
                        onMouseOver={e => e.target.style.background = '#bd2130'}
                        onMouseOut={e => e.target.style.background = '#dc3545'}>
                        ← Salir
                    </button>
                </div>

                {/* Register button */}
                <button
                    onClick={handleOpenRegForm}
                    disabled={registering || !currentPosition}
                    style={{
                        position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 1000, background: registering ? '#6c757d' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                        color: '#fff', border: 'none', padding: '16px 36px', borderRadius: 30,
                        fontWeight: 800, fontSize: 16, cursor: registering ? 'not-allowed' : 'pointer',
                        boxShadow: '0 8px 25px rgba(37,99,235,0.4)', whiteSpace: 'nowrap',
                        transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseDown={e => e.target.style.transform = 'translateX(-50%) scale(0.95)'}
                    onMouseUp={e => e.target.style.transform = 'translateX(-50%) scale(1)'}
                >
                    {registering ? '⏳ Registrando...' : '📍 Registrar Punto PE / PI'}
                </button>

                {/* Formulario flotante Premium */}
                {showRegForm && (
                    <div style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        zIndex: 2000, width: '90%', maxWidth: '500px', background: '#ffffff',
                        borderRadius: 20, boxShadow: '0 15px 50px rgba(0,0,0,0.4)', padding: 24,
                        color: '#333', overflowY: 'auto', maxHeight: '80vh', border: '1px solid #eee'
                    }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: 18, color: '#1e3a8a', textAlign: 'center', fontWeight: 800 }}>
                            Registrar Nuevo Punto en Recorrido
                        </h3>

                        {/* Selección Tipo de Red */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#666', marginBottom: 8 }}>
                                TIPO DE RED DE PLANTA
                            </label>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setNetworkType('aereo');
                                        setSelectedIncidences([]); // Limpiar incidencias en aéreo
                                    }}
                                    style={{
                                        flex: 1, padding: 14, borderRadius: 12, border: networkType === 'aereo' ? '2.5px solid #2563eb' : '1px solid #ccc',
                                        background: networkType === 'aereo' ? '#eff6ff' : '#fff', cursor: 'pointer',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, transition: 'all 0.2s'
                                    }}
                                >
                                    <span style={{ fontSize: 24 }}>⚡</span>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: networkType === 'aereo' ? '#1e3a8a' : '#555' }}>Aéreo</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setNetworkType('canalizado');
                                        setSelectedIncidences([]); // Limpiar incidencias al cambiar
                                    }}
                                    style={{
                                        flex: 1, padding: 14, borderRadius: 12, border: networkType === 'canalizado' ? '2.5px solid #2563eb' : '1px solid #ccc',
                                        background: networkType === 'canalizado' ? '#eff6ff' : '#fff', cursor: 'pointer',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, transition: 'all 0.2s'
                                    }}
                                >
                                    <span style={{ fontSize: 24 }}>⛖</span>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: networkType === 'canalizado' ? '#1e3a8a' : '#555' }}>Canalizado</span>
                                </button>
                            </div>
                        </div>

                        {/* Selección de Incidencias */}
                        {networkType === 'canalizado' && (
                            <div style={{ marginBottom: 24 }}>
                                <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#666', marginBottom: 12 }}>
                                    SELECCIONAR INCIDENCIAS (OPCIONAL)
                                </label>

                                {/* PLANTA EXTERNA */}
                                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 14, border: '1px solid #e2e8f0' }}>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c', display: 'block', marginBottom: 8, letterSpacing: '0.5px' }}>
                                        PLANTA EXTERNA (PE)
                                    </span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {PE_INCIDENCES.map(inc => (
                                            <label key={inc} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#334155', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIncidences.includes(inc)}
                                                    onChange={() => handleToggleIncidence(inc)}
                                                    style={{ width: 16, height: 16, marginTop: 1 }}
                                                />
                                                <span>{inc}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* PLANTA INTERNA */}
                                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 14, border: '1px solid #e2e8f0' }}>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#0369a1', display: 'block', marginBottom: 8, letterSpacing: '0.5px' }}>
                                        PLANTA INTERNA (PI)
                                    </span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {PI_INCIDENCES.map(inc => (
                                            <label key={inc} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#334155', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIncidences.includes(inc)}
                                                    onChange={() => handleToggleIncidence(inc)}
                                                    style={{ width: 16, height: 16, marginTop: 1 }}
                                                />
                                                <span>{inc}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {networkType === 'aereo' && (
                            <div style={{ marginBottom: 24 }}>
                                <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#666', marginBottom: 12 }}>
                                    SELECCIONAR INCIDENCIAS (OPCIONAL)
                                </label>

                                {/* PLANTA INTERNA */}
                                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 14, border: '1px solid #e2e8f0' }}>
                                    <span style={{ fontSize: 12, fontWeight: 800, color: '#0369a1', display: 'block', marginBottom: 8, letterSpacing: '0.5px' }}>
                                        PLANTA INTERNA (PI)
                                    </span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {AEREO_PI_INCIDENCES.map(inc => (
                                            <label key={inc} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#334155', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIncidences.includes(inc)}
                                                    onChange={() => handleToggleIncidence(inc)}
                                                    style={{ width: 16, height: 16, marginTop: 1 }}
                                                />
                                                <span>{inc}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Cargar Foto (Siempre obligatoria) */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#666', marginBottom: 8 }}>
                                📸 FOTO DE EVIDENCIA DEL REGISTRO (OBLIGATORIA)
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {preview ? (
                                    <div style={{ position: 'relative', width: '100%', height: 160, borderRadius: 12, overflow: 'hidden', border: '1px solid #ccc' }}>
                                        <img src={preview} alt="Vista previa de evidencia" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <button
                                            type="button"
                                            onClick={removePhoto}
                                            style={{
                                                position: 'absolute', top: 10, right: 10, background: 'rgba(220,53,69,0.9)', color: '#fff',
                                                border: 'none', borderRadius: '50%', width: 30, height: 30, fontSize: 16, fontWeight: 700,
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                                            }}
                                        >✕</button>
                                    </div>
                                ) : compressing ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '16px', background: '#eff6ff', borderRadius: '12,', border: '2px dashed #2563eb', gap: '8px', boxSizing: 'border-box' }}>
                                        <span style={{ fontSize: '24px' }}>⏳</span>
                                        <span style={{ fontSize: '13px', fontWeight: 800, color: '#2563eb' }}>Procesando y comprimiendo foto...</span>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                                        <label style={{
                                            flex: 1, padding: '14px 8px', borderRadius: 12, border: '2px dashed #2563eb', background: '#eff6ff',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s'
                                        }}
                                            onMouseOver={e => e.currentTarget.style.background = '#dbeafe'}
                                            onMouseOut={e => e.currentTarget.style.background = '#eff6ff'}
                                        >
                                            <span style={{ fontSize: 28 }}>📷</span>
                                            <span style={{ fontSize: 12.5, fontWeight: 800, color: '#2563eb' }}>Cámara</span>
                                            <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                                        </label>
                                        <label style={{
                                            flex: 1, padding: '14px 8px', borderRadius: 12, border: '2px dashed #8b5cf6', background: '#f5f3ff',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s'
                                        }}
                                            onMouseOver={e => e.currentTarget.style.background = '#ede9fe'}
                                            onMouseOut={e => e.currentTarget.style.background = '#f5f3ff'}
                                        >
                                            <span style={{ fontSize: 28 }}>🖼️</span>
                                            <span style={{ fontSize: 12.5, fontWeight: 800, color: '#7c3aed' }}>Galería</span>
                                            <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Mensaje de Error */}
                        {errorMsg && (
                            <div style={{
                                background: '#fef2f2', color: '#b91c1c', border: '1px solid #fee2e2',
                                padding: '10px 14px', borderRadius: 10, fontSize: 12.5, fontWeight: 600,
                                marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
                            }}>
                                <span>⚠️</span>
                                <span>{errorMsg}</span>
                            </div>
                        )}

                        {/* Botones */}
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button
                                type="button"
                                onClick={() => setShowRegForm(false)}
                                style={{
                                    flex: 1, padding: 12, borderRadius: 12, border: '1px solid #ccc',
                                    background: '#f1f5f9', color: '#475569', fontWeight: 700, cursor: 'pointer',
                                    fontSize: 14
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSavePoint}
                                disabled={registering || compressing || !photo}
                                style={{
                                    flex: 1, padding: 12, borderRadius: 12, border: 'none',
                                    background: (registering || compressing || !photo) ? '#94a3b8' : 'linear-gradient(135deg, #10b981, #059669)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: (registering || compressing || !photo) ? 'not-allowed' : 'pointer',
                                    fontSize: 14,
                                    boxShadow: (registering || compressing || !photo) ? 'none' : '0 4px 12px rgba(16,185,129,0.3)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {registering ? '⏳ Guardando...' : compressing ? '⏳ Procesando foto...' : !photo ? '📷 Toma la Foto Primero' : 'Guardar Punto'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Segment summary (fixed below map) */}
            <InspectorLoadingOverlay
                active={registering || compressing}
                message={compressing ? "Analizando y comprimiendo foto de evidencia..." : "Enviando evidencia y registrando punto de red..."}
            />
            <div style={{
                background: '#131324', color: '#fff', padding: '14px 18px',
                maxHeight: '35vh', overflowY: 'auto', borderTop: '3px solid #2563eb'
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 12, flexWrap: 'wrap', gap: 8
                }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>📊 Resumen de Recorrido</h3>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            padding: '4px 10px', borderRadius: 16, fontSize: 12, color: '#cbd5e1'
                        }}>
                            Bruto: {totalDistance >= 1000 ? `${(totalDistance / 1000).toFixed(2)} km` : `${totalDistance} m`}
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            padding: '6px 14px', borderRadius: 20, fontWeight: 700, fontSize: 13,
                            boxShadow: '0 2px 6px rgba(5,150,105,0.4)'
                        }}>
                            Efectivo: {effectiveDistance >= 1000 ? `${(effectiveDistance / 1000).toFixed(2)} km` : `${effectiveDistance} m`}
                        </div>
                    </div>
                </div>

                {segments.length === 0 ? (
                    <p style={{ color: '#aaa', textAlign: 'center', margin: '15px 0', fontSize: 13.5 }}>
                        No hay puntos registrados aún. Muévete y registra tu primer punto.
                    </p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {segments.map((seg, i) => {
                            const hasInc = seg.incidences && seg.incidences.length > 0;
                            const photoUrl = seg.is_offline && seg.photo_blob
                                ? (seg._localUrl || (seg._localUrl = getLocalObjectUrl(seg.photo_blob)))
                                : seg.photo_path
                                    ? `${baseUrl}/static/uploads/${seg.photo_path}`
                                    : null;

                            return (
                                <div key={seg.id} style={{
                                    display: 'flex', flexDirection: 'column', gap: 6,
                                    background: 'rgba(255,255,255,0.04)', padding: '10px 14px', borderRadius: 12,
                                    borderLeft: `4px solid ${seg.is_offline ? '#f59e0b' : hasInc ? '#ef4444' : '#2563eb'}`,
                                    transition: 'background 0.2s'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            {photoUrl ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedImage(photoUrl)}
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                >
                                                    <img
                                                        src={photoUrl}
                                                        loading="lazy"
                                                        decoding="async"
                                                        alt="Miniatura"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                        style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.15)' }}
                                                    />
                                                    <div style={{ display: 'none', width: 40, height: 40, borderRadius: 6, background: 'rgba(255,255,255,0.08)', alignItems: 'center', justifyContent: 'center', fontSize: 18, border: '1px dashed rgba(255,255,255,0.2)' }} title="Evidencia offline">
                                                        📷
                                                    </div>
                                                </button>
                                            ) : (
                                                <div style={{ width: 40, height: 40, borderRadius: 6, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, border: '1px dashed rgba(255,255,255,0.2)' }} title="Evidencia registrada">
                                                    📷
                                                </div>
                                            )}
                                            <div>
                                                <span style={{ fontWeight: 700, fontSize: 13.5 }}>
                                                    Punto #{i + 1}
                                                </span>
                                                <span style={{
                                                    fontSize: 10.5,
                                                    background: seg.network_type === 'canalizado' ? '#8b5cf6' : '#2563eb',
                                                    color: '#fff',
                                                    padding: '2px 8px',
                                                    borderRadius: 12,
                                                    marginLeft: 8,
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {seg.network_type === 'canalizado' ? 'Canalizado' : 'Aéreo'}
                                                </span>
                                                <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8 }}>
                                                    {new Date(seg.recorded_at).toLocaleTimeString('es-MX')}
                                                </span>
                                            </div>
                                        </div>
                                        <span style={{
                                            fontWeight: 700, fontSize: 13.5,
                                            color: i === 0 ? '#FF9800' : '#4CAF50'
                                        }}>
                                            {i === 0 ? 'Inicio' : `+${seg.distance_from_previous}m`}
                                        </span>
                                    </div>

                                    {hasInc && (
                                        <div style={{
                                            fontSize: 11.5,
                                            color: '#fca5a5',
                                            fontWeight: 600,
                                            padding: '4px 8px',
                                            background: 'rgba(239,68,68,0.1)',
                                            borderRadius: 6,
                                            display: 'inline-block',
                                            width: 'fit-content'
                                        }}>
                                            ⚠️ Incidencias: {seg.incidences.join(', ')}
                                        </div>
                                    )}

                                    {/* Insignia Offline y Botón de envío individual por segmento */}
                                    {seg.is_offline && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            marginTop: 6, paddingTop: 6, borderTop: '1px dashed rgba(255,255,255,0.1)'
                                        }}>
                                            <span style={{
                                                fontSize: 11, background: '#f59e0b', color: '#000',
                                                padding: '2px 8px', borderRadius: 10, fontWeight: 700
                                            }}>
                                                📴 Offline (Pendiente)
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleSyncSingleSegment(seg)}
                                                disabled={syncingSegId !== null}
                                                style={{
                                                    background: syncingSegId === seg.id ? '#6c757d' : syncingSegId !== null ? '#475569' : 'linear-gradient(135deg, #10b981, #059669)',
                                                    color: '#fff', border: 'none', padding: '5px 14px', borderRadius: 8,
                                                    fontSize: 12, fontWeight: 700,
                                                    cursor: syncingSegId !== null ? 'not-allowed' : 'pointer',
                                                    boxShadow: '0 2px 6px rgba(16,185,129,0.3)',
                                                    opacity: syncingSegId !== null && syncingSegId !== seg.id ? 0.6 : 1
                                                }}
                                            >
                                                {syncingSegId === seg.id ? '⏳ Enviando...' : '🔄 Enviar'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal de visualización de imagen ampliada con soporte de botón Atrás del celular y tache X */}
            <ImageViewerModal src={expandedImage} onClose={() => setExpandedImage(null)} />

            {/* Modal de alerta nativo PWA / Móvil estilizado (sin dominios 'dice') */}
            <CustomAlertModal
                show={customAlert.show}
                title={customAlert.title}
                message={customAlert.message}
                type={customAlert.type}
                onClose={hideAlert}
            />
        </div>
    );
};

export default InspectorPEMapModal;
